/**
 * The choices behind the Workspace operating-model form.
 *
 * Every one of these fields was a free-text box holding a value from a fixed
 * set — `tenant_controlled`, `monthly`, `new`, `IN`. Typing `Monthly` instead of
 * `monthly` stored a payroll cycle nothing downstream recognises, and the server
 * accepts it: `update_tenant_operating_model` merges whatever JSON arrives and
 * validates none of it. So the control is the only thing standing between a
 * typo and a silently wrong workspace.
 *
 * The lists live here rather than inline so the same option set can be reused by
 * the superadmin's copy of this form, and so adding a value is one edit.
 *
 * They are **suggestions, not a whitelist**: `SelectField` keeps whatever the
 * workspace already has, even if it is not listed. Turning a free-text field
 * into a closed dropdown would otherwise silently rewrite existing
 * configuration the first time somebody opened the page and pressed Save.
 */

export const MANAGER_ASSIGNMENT_MODES = [
  { value: 'tenant_controlled', label: 'Tenant controlled — admins set the manager' },
  { value: 'self_service', label: 'Self service — employees pick their manager' },
  { value: 'hr_approved', label: 'HR approved — requested, then confirmed by HR' },
  { value: 'sync_from_idp', label: 'From the identity provider' },
];

export const ORG_STRUCTURE_MODES = [
  { value: 'department_subdepartment_designation', label: 'Department → Sub-department → Designation' },
  { value: 'department_designation', label: 'Department → Designation' },
  { value: 'business_unit_department', label: 'Business unit → Department' },
  { value: 'flat', label: 'Flat — no hierarchy' },
];

export const DESIGNATION_RULE_MODES = [
  { value: 'tenant_defined', label: 'Defined by this workspace' },
  { value: 'platform_master', label: 'From the platform master list' },
  { value: 'free_text', label: 'Free text' },
];

export const TEAM_PERMISSION_MODELS = [
  { value: 'scoped_roles', label: 'Scoped roles — permissions follow the reporting line' },
  { value: 'flat_roles', label: 'Flat roles — the same everywhere' },
  { value: 'custom_groups', label: 'Custom groups' },
];

export const LEAVE_RULE_VISIBILITY = [
  { value: 'all_employees', label: 'Everyone' },
  { value: 'managers_only', label: 'Managers only' },
  { value: 'hr_only', label: 'HR only' },
];

export const ONBOARDING_JOURNEYS = [
  { value: 'standard', label: 'Standard' },
  { value: 'campus', label: 'Campus hire' },
  { value: 'lateral', label: 'Lateral hire' },
  { value: 'contractor', label: 'Contractor' },
];

export const WELCOME_EMAIL_VARIANTS = [
  { value: 'default', label: 'Default' },
  { value: 'formal', label: 'Formal' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'minimal', label: 'Minimal' },
];

export const PAY_CYCLES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'semi_monthly', label: 'Twice a month' },
  { value: 'biweekly', label: 'Every two weeks' },
  { value: 'weekly', label: 'Weekly' },
];

export const TAX_REGIMES = [
  { value: 'new', label: 'New regime' },
  { value: 'old', label: 'Old regime' },
  { value: 'employee_choice', label: 'Employee chooses' },
];

export const COMPLIANCE_PROFILES = [
  { value: 'india_standard', label: 'India — standard' },
  { value: 'india_sez', label: 'India — SEZ' },
  { value: 'uae_standard', label: 'UAE — standard' },
  { value: 'singapore_standard', label: 'Singapore — standard' },
  { value: 'uk_standard', label: 'United Kingdom — standard' },
  { value: 'us_standard', label: 'United States — standard' },
];

export const WORKING_CALENDARS = [
  { value: 'Standard 5 Day Week', label: 'Standard 5-day week' },
  { value: 'Six Day Week', label: 'Six-day week' },
  { value: 'Alternate Saturdays', label: 'Alternate Saturdays off' },
  { value: 'Shift Rotation', label: 'Shift rotation' },
];

export const WEEKDAYS = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
];

//: Approval depth is a small integer, and "5 levels" is already pathological.
export const APPROVAL_LEVELS = [1, 2, 3, 4, 5].map((n) => ({
  value: n,
  label: n === 1 ? '1 level' : `${n} levels`,
}));

export const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi' },
  { value: 'ta', label: 'Tamil' },
  { value: 'te', label: 'Telugu' },
  { value: 'mr', label: 'Marathi' },
  { value: 'bn', label: 'Bengali' },
  { value: 'ar', label: 'Arabic' },
  { value: 'fr', label: 'French' },
  { value: 'es', label: 'Spanish' },
];

export const REGIONS = [
  { value: 'IN', label: 'India' },
  { value: 'AE', label: 'United Arab Emirates' },
  { value: 'SG', label: 'Singapore' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'US', label: 'United States' },
  { value: 'AU', label: 'Australia' },
  { value: 'CA', label: 'Canada' },
  { value: 'DE', label: 'Germany' },
];

//: The zones this product's tenants are actually in. Not the full IANA list —
//: 400 options in a native select is not a control, it is a haystack.
export const TIMEZONES = [
  'UTC',
  'Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Asia/Manila', 'Asia/Tokyo',
  'Europe/London', 'Europe/Berlin', 'Europe/Paris',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'Australia/Sydney',
].map((zone) => ({ value: zone, label: zone.replace('_', ' ') }));

export const CURRENCIES = [
  { value: 'INR', label: 'INR — Indian Rupee' },
  { value: 'USD', label: 'USD — US Dollar' },
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'GBP', label: 'GBP — Pound Sterling' },
  { value: 'AED', label: 'AED — UAE Dirham' },
  { value: 'SGD', label: 'SGD — Singapore Dollar' },
  { value: 'AUD', label: 'AUD — Australian Dollar' },
  { value: 'CAD', label: 'CAD — Canadian Dollar' },
];
